import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import API_CONFIG from '../config/api';
import CreateAdminModal from '../components/admin/CreateAdminModal';
import CreateUserModal from '../components/admin/CreateUserModal';
import EditAdminModal from '../components/admin/EditAdminModal';
import EditUserModal from '../components/admin/EditUserModal';
import ViewUserModal from '../components/admin/ViewUserModal';
import {
    AnalyticsTab,
    PermissionsTab,
    RolesTab,
    SettingsTab,
    DatabaseTab,
    SecurityTab,
    ActivityMonitorTab,
    ReportsTab,
    TemplatesTab,
    SupportTab,
    FinancialTab,
    ApiManagementTab,
    NotificationsTab,
    HealthTab,
    PaymentGatewayTab,
    KycManagementTab,
    LoansTab,
    LeadsTab
} from '../components/admin/AdminTabs';
import './SuperAdminDashboardPage.css';
import wsService from '../utils/websocketService';

function SuperAdminDashboardPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [adminSession, setAdminSession] = useState(null);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

    // Permission check helper
    const hasPermission = (permission) => {
        if (!adminSession) return false;
        if (adminSession.role === 'super_admin') return true;

        const userPerms = adminSession.permissions || [];
        if (Array.isArray(permission)) {
            return permission.some(p => userPerms.includes(p));
        }
        return userPerms.includes(permission);
    };

    // Tab visibility mapping
    const tabPermissions = {
        'overview': null, // Everyone can see overview
        'admins': 'admins.view',
        'users': ['users.view', 'users.view_all', 'users.manage'],
        'logs': 'logs.view',
        'analytics': 'analytics.view',
        'permissions': 'permissions.manage',
        'roles': 'roles.manage',
        'settings': 'settings.manage',
        'database': 'database.manage',
        'security': 'security.manage',
        'activity': 'activity.view',
        'reports': 'reports.view',
        'templates': 'templates.manage',
        'support': 'tickets.manage',
        'financials': 'reports.view',
        'api': 'api.manage',
        'notifications': 'notifications.manage',
        'health': 'system.health',
        'payments': ['payments', 'payments.manage', 'payments.view'],
        'kyc': 'kyc.manage',
        'loans': ['loans.view', 'loans.edit', 'loans.approve', 'loans.manage'],
        'leads': ['leads.view', 'leads.manage', 'leads.edit']
    };
    const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [showEditAdminModal, setShowEditAdminModal] = useState(false);
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const [showViewUserModal, setShowViewUserModal] = useState(false);

    // Sync activeTab with URL params
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && tab !== activeTab) {
            setActiveTab(tab);
        }
    }, [searchParams, activeTab]);

    // Role-based default landing tab when no tab is specified
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam || !adminSession) return;
        const role = adminSession.role;
        if (role === 'sales') {
            setActiveTab('leads');
        } else if (role === 'branch' || role === 'collection' || role === 'accounts') {
            setActiveTab('loans');
        }
    }, [adminSession, searchParams]);

    // Update URL when activeTab changes (for internal tab clicks)
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    // Helpers to format and derive overview KPIs
    const parseCurrencyToNumber = (value) => {
        if (typeof value === 'number') return value;
        if (!value) return 0;
        const numeric = String(value).replace(/[^\d.]/g, '');
        return Number(numeric) || 0;
    };

    const [stats, setStats] = useState({
        totalUsers: 0,
        totalAdmins: 0,
        activeLoans: 0,
        pendingApprovals: 0,
        pendingLoans: 0,
        totalLoans: 0,
        openTickets: 0,
        totalDisbursed: '₹0',
        systemHealth: 'Excellent'
    });
    const [leadSummary, setLeadSummary] = useState({
        total: 0,
        byStatus: {},
        myCount: 0,
        unassigned: 0
    });

    const [admins, setAdmins] = useState([]);
    const [recentUsers, setRecentUsers] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [systemLogs, setSystemLogs] = useState([]);
    const [logFilter, setLogFilter] = useState('');
    const [logSeverity, setLogSeverity] = useState('all');
    // eslint-disable-next-line no-unused-vars
    const [isLoading, setIsLoading] = useState(true);
    const [timeframe, setTimeframe] = useState('7d');

    // Custom Modal State
    const [modalConfig, setModalConfig] = useState({
        show: false,
        type: 'confirm', // 'confirm' or 'alert'
        title: '',
        message: '',
        onConfirm: null,
        confirmText: 'Confirm',
        cancelText: 'Cancel'
    });

    // Derived KPI helpers for richer dashboard storytelling
    const totalLoanCount = stats.totalLoans || 0;
    const approvalRate = totalLoanCount ? Math.round((stats.activeLoans / totalLoanCount) * 100) : 0;
    const pendingRate = totalLoanCount ? Math.round((stats.pendingLoans / totalLoanCount) * 100) : 0;
    const avgDisbursalPerUser = stats.totalUsers ? Math.round(parseCurrencyToNumber(stats.totalDisbursed) / Math.max(stats.totalUsers, 1)) : 0;
    const openTicketBacklog = stats.openTickets || stats.pendingApprovals || 0;
    const avgTicketSize = stats.activeLoans ? Math.round(parseCurrencyToNumber(stats.totalDisbursed) / Math.max(stats.activeLoans, 1)) : 0;
    const conversionRate = totalLoanCount ? Math.round((stats.activeLoans / totalLoanCount) * 100) : 0;
    const complianceScore = 100 - pendingRate;

    const showConfirm = (title, message, onConfirm) => {
        setModalConfig({
            show: true,
            type: 'confirm',
            title,
            message,
            onConfirm,
            confirmText: 'Confirm',
            cancelText: 'Cancel'
        });
    };

    const showAlert = (title, message) => {
        setModalConfig({
            show: true,
            type: 'alert',
            title,
            message,
            onConfirm: null,
            confirmText: 'OK',
            cancelText: ''
        });
    };

    const closeModal = () => {
        setModalConfig(prev => ({ ...prev, show: false }));
    };
    // Always exclude admin users from User Management page (show only regular client users)
    const includeAdminsInUsers = false;

    const [editingAdmin, setEditingAdmin] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [viewingUser, setViewingUser] = useState(null);
    const [showTempPassModal, setShowTempPassModal] = useState(false);
    const [tempPassUser, setTempPassUser] = useState(null);
    const [tempPassword, setTempPassword] = useState('');
    const [tempValidity, setTempValidity] = useState(10);

    useEffect(() => {
        // Check for impersonation token in URL
        const urlParams = new URLSearchParams(window.location.search);
        const impersonateToken = urlParams.get('impersonate');

        if (impersonateToken) {
            try {
                // Decode the token
                const tokenData = JSON.parse(atob(impersonateToken));
                const { session, timestamp } = tokenData;

                // Check if token is not expired (valid for 5 minutes)
                const tokenAge = Date.now() - timestamp;
                if (tokenAge > 5 * 60 * 1000) {
                    showAlert('⚠️ Token Expired', 'The impersonation link has timed out for your security. Please generate a new one.');
                    // Remove token from URL
                    urlParams.delete('impersonate');
                    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
                } else {
                    // Apply the impersonated session
                    const sessionKey = session.isAdmin ? 'adminSession' : 'userSession';
                    localStorage.setItem(sessionKey, JSON.stringify(session));

                    console.log(`🔌 Impersonated as: ${session.name}`);
                    console.log('Session:', session);

                    // Remove token from URL and reload
                    urlParams.delete('impersonate');
                    const newUrl = urlParams.toString()
                        ? `${window.location.pathname}?${urlParams.toString()}`
                        : window.location.pathname;
                    window.history.replaceState({}, '', newUrl);
                    window.location.reload();
                    return;
                }
            } catch (error) {
                console.error('Error applying impersonation token:', error);
                showAlert('❌ Link Compromised', 'The security token provided is invalid or has been tampered with.');
            }
        }

        const session = localStorage.getItem('adminSession');
        if (session) {
            const parsed = JSON.parse(session);
            setAdminSession(parsed);
            // Connect WebSocket for real-time updates and register named handlers (so we can remove on cleanup)
            wsService.connect(parsed.userId, parsed.role === 'super_admin')
                .then(() => {
                    // named handlers
                    const handleUserUpdated = () => fetchAllData();
                    const handleUserDeleted = () => fetchAllData();
                    const handleAdminCreated = () => fetchAllData();
                    const handleAdminUpdated = (payload) => {
                        // Refresh dashboard lists for everyone
                        fetchAllData();

                        // If the updated admin is ME, refresh my own session permissions instantly
                        const currentSession = JSON.parse(localStorage.getItem('adminSession'));
                        if (payload && payload.adminId === currentSession?.userId) {
                            console.log('⚡ Instant Permission Sync: Updating local session');
                            const updatedSession = {
                                ...currentSession,
                                permissions: payload.permissions || currentSession.permissions,
                                role: payload.role || currentSession.role,
                                level: payload.level || currentSession.level,
                                name: payload.name || currentSession.name
                            };
                            localStorage.setItem('adminSession', JSON.stringify(updatedSession));
                            setAdminSession(updatedSession);

                            // Optional: Show a subtle notification
                            showAlert('⚡ Security Update', 'Your administrative permissions have been updated by the system core.');
                        }
                    };
                    const handleAdminDeleted = () => fetchAllData();
                    const handleLoanUpdated = () => fetchAllData();
                    const handleTicketUpdated = () => fetchAllData();
                    const handleStatsUpdated = () => fetchAllData();

                    wsService.on('user.updated', handleUserUpdated);
                    wsService.on('user.deleted', handleUserDeleted);
                    wsService.on('admin.created', handleAdminCreated);
                    wsService.on('admin.updated', handleAdminUpdated);
                    wsService.on('admin.deleted', handleAdminDeleted);
                    wsService.on('loan.updated', handleLoanUpdated);
                    wsService.on('ticket.updated', handleTicketUpdated);
                    wsService.on('stats.updated', handleStatsUpdated);

                    // store handlers on the service so cleanup can reference them
                    wsService._handlers = {
                        handleUserUpdated,
                        handleUserDeleted,
                        handleAdminCreated,
                        handleAdminUpdated,
                        handleAdminDeleted,
                        handleLoanUpdated,
                        handleTicketUpdated,
                        handleStatsUpdated
                    };
                })
                .catch(err => console.error('WebSocket connect error:', err));
        }

        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSearch]);

    // Cleanup websocket listeners on unmount
    useEffect(() => {
        return () => {
            if (wsService._handlers) {
                const h = wsService._handlers;
                wsService.off('user.updated', h.handleUserUpdated);
                wsService.off('user.deleted', h.handleUserDeleted);
                wsService.off('admin.created', h.handleAdminCreated);
                wsService.off('admin.updated', h.handleAdminUpdated);
                wsService.off('admin.deleted', h.handleAdminDeleted);
                wsService.off('loan.updated', h.handleLoanUpdated);
                wsService.off('ticket.updated', h.handleTicketUpdated);
                wsService.off('stats.updated', h.handleStatsUpdated);
                delete wsService._handlers;
            }
            try { wsService.disconnect(); } catch (e) { }
        };
    }, []);

    const fetchAllData = async () => {
        setIsLoading(true);
        try {
            const adminSessionObj = JSON.parse(localStorage.getItem('adminSession'));
            const userId = adminSessionObj?.userId;

            // Fetch Stats
            const statsRes = await fetch(`${API_CONFIG.BASE_URL}/api/admin/stats`);
            const statsData = await statsRes.json();
            if (statsData.success) {
                setStats({
                    ...statsData.data,
                    systemHealth: 'Excellent',
                    pendingApprovals: statsData.data.pendingLoans || 0,
                    totalAdmins: 0 // Will be updated by admins fetch
                });
            }

            // Fetch Admins
            const adminsRes = await fetch(`${API_CONFIG.BASE_URL}/api/admin/admins`, {
                headers: { 'x-user-id': userId }
            });
            const adminsData = await adminsRes.json();
            if (adminsData.success) {
                const formattedAdmins = adminsData.data.map(a => ({
                    id: a.id,
                    name: `${a.first_name} ${a.last_name}`,
                    email: a.email,
                    phone: a.phone,
                    role: a.role_name,
                    status: a.is_active ? 'Active' : 'Inactive',
                    lastLogin: a.last_login_at ? new Date(a.last_login_at).toLocaleString() : 'Never'
                }));
                setAdmins(formattedAdmins);
                // stats.totalAdmins will be provided by backend stats endpoint too, but let's keep this sync
                setStats(prev => ({ ...prev, totalAdmins: Math.max(prev.totalAdmins || 0, formattedAdmins.length) }));
            }

            // Fetch Users (exclude admin accounts by default)
            const usersRes = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users?limit=10&includeAdmins=${includeAdminsInUsers}&search=${encodeURIComponent(userSearch || '')}`);
            const usersData = await usersRes.json();
            if (usersData.success) {
                const formattedUsers = usersData.data.users.map(u => ({
                    id: u.id,
                    name: `${u.first_name} ${u.last_name}`,
                    email: u.email,
                    phone: u.phone,
                    status: u.is_active ? 'Active' : 'Inactive',
                    kyc: u.kyc_status,
                    loans: 0,
                    joinDate: new Date(u.created_at).toLocaleDateString()
                }));
                setRecentUsers(formattedUsers);
            }

            // Activity Logs (live)
            try {
                const logsRes = await fetch(`${API_CONFIG.BASE_URL}/api/admin/activity`, {
                    headers: { 'x-user-id': userId }
                });
                const logsData = await logsRes.json();
                if (logsData.success && Array.isArray(logsData.data)) {
                    const formattedLogs = logsData.data.map(l => ({
                        id: l.id,
                        action: l.action_type || 'activity',
                        user: l.admin_name || l.admin_email || 'Unknown',
                        details: l.description || '',
                        time: l.created_at,
                        severity: (l.severity || 'info').toLowerCase(),
                        target: l.target_type ? `${l.target_type} #${l.target_id || ''}`.trim() : ''
                    }));
                    setSystemLogs(formattedLogs);
                }
            } catch (e) {
                console.error('Logs fetch error', e);
            }

            // Lead snapshot for role dashboards (sales/branch/collection)
            if (adminSessionObj && (adminSessionObj.role === 'sales' || adminSessionObj.role === 'branch' || adminSessionObj.role === 'collection' || adminSessionObj.role === 'super_admin' || adminSessionObj.role === 'admin' || adminSessionObj.permissions?.includes?.('leads.view'))) {
                const leadsRes = await fetch(`${API_CONFIG.BASE_URL}/api/admin/leads`, {
                    headers: { 'x-user-id': userId }
                });
                const leadsData = await leadsRes.json();
                if (leadsData.success && Array.isArray(leadsData.data)) {
                    const allLeads = leadsData.data;
                    const byStatus = allLeads.reduce((acc, lead) => {
                        acc[lead.status] = (acc[lead.status] || 0) + 1;
                        return acc;
                    }, {});
                    const myCount = allLeads.filter(l => `${l.assigned_to}` === `${userId}`).length;
                    const unassigned = allLeads.filter(l => !l.assigned_to).length;
                    setLeadSummary({
                        total: allLeads.length,
                        byStatus,
                        myCount,
                        unassigned
                    });
                }
            }

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Cleanup WebSocket on unmount
    useEffect(() => {
        return () => {
            try {
                wsService.disconnect();
            } catch (e) {
                // ignore
            }
        };
    }, []);

    const handleEditAdmin = async (admin) => {
        setEditingAdmin(admin);
        setShowEditAdminModal(true);
    };

    const handleDeleteAdmin = (adminId) => {
        showConfirm(
            'Delete Admin Account',
            'Are you sure you want to delete this admin? This action cannot be undone and will un-link all associated records.',
            async () => {
                try {
                    const adminSessionObj = JSON.parse(localStorage.getItem('adminSession'));
                    const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/admins/${adminId}`, {
                        method: 'DELETE',
                        headers: { 'x-user-id': adminSessionObj?.userId }
                    });
                    const data = await res.json();
                    if (data.success) {
                        showAlert('Success', 'Admin account has been permanently removed.');
                        fetchAllData();
                    } else {
                        showAlert('Error', data.message || 'Failed to delete admin');
                    }
                } catch (error) {
                    showAlert('Server Error', 'Failed to connect to the security core. Please check your connection.');
                }
            }
        );
    };

    const toggleAdminStatus = async (adminId, currentStatus) => {
        const newStatus = currentStatus === 'Active' ? false : true;
        try {
            const adminSessionObj = JSON.parse(localStorage.getItem('adminSession'));
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/admins/${adminId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSessionObj?.userId
                },
                body: JSON.stringify({ is_active: newStatus })
            });
            const data = await res.json();
            if (data.success) {
                fetchAllData();
            } else {
                showAlert('Status Update Failed', data.message || 'Could not update admin status');
            }
        } catch (error) {
            showAlert('Connection Error', 'Failed to reach the server.');
        }
    };

    const handleViewUser = (user) => {
        setViewingUser(user);
        setShowViewUserModal(true);
    };



    const changeUserStatus = async (userId, val) => {
        if (!val) return;
        const normalized = val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
        // Optimistic UI update
        setRecentUsers(prev => prev.map(u => u.id === userId ? { ...u, status: normalized } : u));
        try {
            const adminSessionObj = JSON.parse(localStorage.getItem('adminSession'));
            if (normalized === 'Restricted') {
                const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${userId}/restrict`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-user-id': adminSessionObj?.userId },
                    body: JSON.stringify({ restricted: true })
                });
                const data = await res.json();
                if (!data.success) {
                    fetchAllData();
                    showAlert('Action Failed', data.message || 'Failed to restrict user');
                }
            } else {
                const isActive = normalized === 'Active';
                const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${userId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'x-user-id': adminSessionObj?.userId },
                    body: JSON.stringify({ is_active: isActive })
                });
                const data = await res.json();
                if (!data.success) {
                    fetchAllData();
                    showAlert('Status Update Failed', data.message || 'Failed to update user status');
                }
            }
        } catch (e) {
            fetchAllData();
            showAlert('Connection Error', 'System unreachable.');
        }
    };



    const handleEditUser = async (user) => {
        setEditingUser(user);
        setShowEditUserModal(true);
    };

    const openTempPasswordModal = (user) => {
        setTempPassUser(user);
        setTempPassword(generateTempPassword());
        setShowTempPassModal(true);
    };

    const handleCreateTempPassword = async () => {
        if (!tempPassUser || !tempPassword) {
            showAlert('Missing details', 'Please set a password first.');
            return;
        }
        try {
            const adminSessionObj = JSON.parse(localStorage.getItem('adminSession'));
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${tempPassUser.id}/temp-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSessionObj?.userId
                },
                body: JSON.stringify({
                    password: tempPassword,
                    minutes: Number(tempValidity)
                })
            });
            const data = await res.json();
            if (data.success) {
                showAlert('Temporary Access Created', `Password valid until ${new Date(data.expiresAt).toLocaleTimeString()}.`);
                setShowTempPassModal(false);
            } else {
                showAlert('Failed', data.message || 'Could not create temporary password.');
            }
        } catch (err) {
            console.error('Temp password error:', err);
            showAlert('Network Error', 'Could not reach server.');
        }
    };

    const handleImpersonate = (targetId, type = 'admin') => {
        showConfirm(
            'Secure Session Impersonation',
            `You are about to establish a secondary neural link as this ${type}. Do you wish to proceed?`,
            async () => {
                try {
                    const adminSessionObj = JSON.parse(localStorage.getItem('adminSession'));
                    const hasImpersonatePerm = adminSessionObj?.role === 'super_admin' || adminSessionObj?.permissions?.includes('admin.impersonate');

                    if (!adminSessionObj || !hasImpersonatePerm || !adminSessionObj.userId) {
                        showAlert('Access Denied', 'Permission admin.impersonate required for neural impersonation.');
                        return;
                    }
                    const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/impersonate/${targetId}`, {
                        method: 'POST',
                        headers: {
                            'x-user-id': adminSessionObj?.userId,
                            'Content-Type': 'application/json'
                        }
                    });
                    const data = await res.json();

                    if (data.success) {
                        // Create session object with timestamps
                        const impersonatedSession = {
                            ...data.data,
                            loginTime: Date.now(),
                            expiresAt: Date.now() + (60 * 60 * 1000), // 1 hour session
                            isImpersonating: true,
                            originalAdminId: adminSessionObj?.userId
                        };

                        // Create a unique token for this impersonation session
                        const impersonationToken = btoa(JSON.stringify({
                            session: impersonatedSession,
                            timestamp: Date.now()
                        }));

                        // Determine the target URL
                        let targetUrl;
                        if (data.data.isAdmin) {
                            targetUrl = `/admin/dashboard?impersonate=${impersonationToken}`;
                            const fullUrl = window.location.origin + targetUrl;
                            window.open(fullUrl, '_blank');
                        } else {
                            // Regular user portal is on port 3000 locally
                            const isLoc = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                            const clientPortalUrl = isLoc ? 'http://localhost:3000' : window.location.origin.replace('admin.', '');
                            targetUrl = `${clientPortalUrl}/dashboard?impersonate=${impersonationToken}`;
                            window.open(targetUrl, '_blank');
                        }

                        showAlert('Connection Established', `Neural link to ${data.data.name} active in new sector.`);
                    } else {
                        showAlert('Impersonation Failed', data.message || 'Identity verification rejected.');
                    }
                } catch (error) {
                    console.error('Impersonation error:', error);
                    showAlert('Critical Error', 'Signal lost during transmission.');
                }
            }
        );
    };

    const handleDeleteUser = (userId) => {
        showConfirm(
            'Delete User Record',
            'Are you sure you want to permanently delete this user? This will erase all history, loans, and personal data.',
            async () => {
                try {
                    const adminSessionObj = JSON.parse(localStorage.getItem('adminSession'));
                    const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${userId}`, {
                        method: 'DELETE',
                        headers: { 'x-user-id': adminSessionObj?.userId }
                    });
                    const data = await res.json();
                    if (data.success) {
                        showAlert('User Deleted', 'Account and associated data moved to void.');
                        fetchAllData();
                    } else {
                        showAlert('Error', data.message || 'User cleanup failed.');
                    }
                } catch (error) {
                    showAlert('Critical Error', 'Database unreachable.');
                }
            }
        );
    };

    /*
    const getStatusClass = (status) => {
        switch (status.toLowerCase()) {
            case 'active':
            case 'approved':
            case 'verified':
            case 'excellent':
                return 'status-success';
            case 'pending':
                return 'status-warning';
            case 'inactive':
            case 'rejected':
                return 'status-danger';
            default:
                return '';
        }
    };
    */

    const getSeverityClass = (severity) => {
        switch (severity) {
            case 'success':
                return 'log-success';
            case 'warning':
                return 'log-warning';
            case 'error':
                return 'log-error';
            default:
                return 'log-info';
        }
    };

    const getRoleBadge = (role) => {
        if (!role) return <span className="role-badge user">User</span>;

        switch (role.toLowerCase()) {
            case 'super_admin':
                return <span className="role-badge super-admin">Super Admin</span>;
            case 'admin':
                return <span className="role-badge admin">Admin</span>;
            case 'sales':
                return <span className="role-badge sales">Sales Staff</span>;
            case 'support':
                return <span className="role-badge support">Support Spec</span>;
            case 'collection':
                return <span className="role-badge support">Collections</span>;
            case 'branch':
                return <span className="role-badge admin">Branch</span>;
            case 'accounts':
                return <span className="role-badge sales">Accounts</span>;
            default:
                return <span className="role-badge">{role}</span>;
        }
    };

    const generateTempPassword = () => {
        const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lower = 'abcdefghijklmnopqrstuvwxyz';
        const nums = '0123456789';
        const special = '!@#$%^&*';
        const pick = (set) => set[Math.floor(Math.random() * set.length)];

        const first = pick(upper); // first letter capital
        const mustNum = pick(nums);
        const mustSpecial = pick(special);
        const pool = upper + lower + nums + special;

        const remaining = Array.from({ length: 5 }, () => pick(pool));
        const chars = [first, mustNum, mustSpecial, ...remaining];
        // shuffle except first stays first? requirement only first letter capital; others any order is fine but keep randomness
        for (let i = 1; i < chars.length; i++) {
            const j = 1 + Math.floor(Math.random() * (chars.length - 1));
            [chars[i], chars[j]] = [chars[j], chars[i]];
        }
        return chars.join('').slice(0, 8);
    };

    const handleAdminCreated = (newAdmin) => {
        fetchAllData();
    };

    const handleUserCreated = (newUser) => {
        fetchAllData();
    };

    return (
        <div className="super-admin-dashboard-page">
            {/* Tab Content */}
            <div className="super-tab-content">
                {activeTab === 'dashboard' && (
                    <div className="dashboard-summary-premium">
                        {/* Live Status Header */}
                        <div className="live-status-header">
                            <div className="live-indicator">
                                <span className="pulse-dot"></span>
                                <span className="live-text">Live System Monitoring</span>
                            </div>
                            <div className="last-updated">
                                Last synced: Just now
                            </div>
                        </div>

                        {/* Enhanced Stats Grid */}
                        <div className="super-stats-grid-premium">
                            <div className="premium-stat-card glass-morph purple">
                                <div className="stat-icon-wrapper">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="9" cy="7" r="4"></circle>
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <p className="stat-label">Total Platform Users</p>
                                    <h3 className="stat-value">{stats.totalUsers.toLocaleString()}</h3>
                                    <div className="stat-trend positive">
                                        <span className="trend-icon">↑</span>
                                        <span className="trend-text">12.4% vs last month</span>
                                    </div>
                                </div>
                            </div>

                            <div className="premium-stat-card glass-morph pink">
                                <div className="stat-icon-wrapper">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <p className="stat-label">Active Capital Outflow</p>
                                    <h3 className="stat-value">{stats.totalDisbursed}</h3>
                                    <div className="stat-trend positive">
                                        <span className="trend-icon">↑</span>
                                        <span className="trend-text">8.2% monthly growth</span>
                                    </div>
                                </div>
                            </div>

                            <div className="premium-stat-card glass-morph blue">
                                <div className="stat-icon-wrapper">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <p className="stat-label">System Security Score</p>
                                    <h3 className="stat-value">98.2%</h3>
                                    <div className="stat-trend neutral">
                                        <span className="trend-icon">●</span>
                                        <span className="trend-text">Enhanced protection active</span>
                                    </div>
                                </div>
                            </div>

                            <div className="premium-stat-card glass-morph green">
                                <div className="stat-icon-wrapper">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <p className="stat-label">Server Response Time</p>
                                    <h3 className="stat-value">240ms</h3>
                                    <div className="stat-trend positive">
                                        <span className="trend-icon">↓</span>
                                        <span className="trend-text">15ms faster than avg</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Analysis Section */}
                        <div className="dashboard-widgets-grid-premium">
                            <div className="widget-card glass-morph analysis-chart">
                                <div className="widget-header">
                                    <h2>Capital Growth Performance</h2>
                                    <div className="widget-actions">
                                        <select className="premium-select" value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                                            <option value="7d">Last 7 Days</option>
                                            <option value="30d">Last 30 Days</option>
                                            <option value="ytd">Year to Date</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="premium-chart-container">
                                    <div className="chart-y-axis">
                                        <span>50Cr</span><span>40Cr</span><span>30Cr</span><span>20Cr</span><span>10Cr</span><span>0</span>
                                    </div>
                                    <div className="chart-bars-wrapper">
                                        {[65, 82, 45, 95, 76, 88, 72].map((height, i) => (
                                            <div key={i} className="chart-bar-group">
                                                <div className="bar-interactive" style={{ height: `${height}% ` }}>
                                                    <div className="bar-tooltip">₹{(height * 0.5).toFixed(1)} Cr</div>
                                                </div>
                                                <span className="bar-label">{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="widget-card glass-morph system-health-check">
                                <div className="widget-header">
                                    <h2>Critical Monitor</h2>
                                </div>
                                <div className="health-metrics-list">
                                    <div className="health-metric-item">
                                        <div className="metric-info">
                                            <span>Database Latency</span>
                                            <span className="metric-status online">Optimal</span>
                                        </div>
                                        <div className="metric-progress-base"><div className="metric-progress-fill" style={{ width: '85%' }}></div></div>
                                    </div>
                                    <div className="health-metric-item">
                                        <div className="metric-info">
                                            <span>API Gateway</span>
                                            <span className="metric-status online">Healthy</span>
                                        </div>
                                        <div className="metric-progress-base"><div className="metric-progress-fill blue" style={{ width: '92%' }}></div></div>
                                    </div>
                                    <div className="health-metric-item">
                                        <div className="metric-info">
                                            <span>Cloud Storage</span>
                                            <span className="metric-status busy">82% Full</span>
                                        </div>
                                        <div className="metric-progress-base"><div className="metric-progress-fill warning" style={{ width: '82%' }}></div></div>
                                    </div>
                                </div>
                                <button className="premium-full-audit-btn">Run Complete System Audit</button>
                            </div>
                        </div>

                        {/* Recent Activity Premium */}
                        <div className="recent-activity-premium glass-morph">
                            <div className="widget-header">
                                <h2>Recent Security & System Events</h2>
                                <button className="text-btn">View Full Audit Trail →</button>
                            </div>
                            <div className="premium-activity-list">
                                {systemLogs.slice(0, 5).map(log => (
                                    <div key={log.id} className={`premium - activity - item ${log.severity} `}>
                                        <div className="activity-status-icon"></div>
                                        <div className="activity-details">
                                            <div className="activity-main">
                                                <strong>{log.action}</strong>
                                                <span className="activity-user">Managed by {log.user}</span>
                                            </div>
                                            <p className="activity-desc">{log.details}</p>
                                        </div>
                                        <div className="activity-time">{log.time}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'overview' && (
                    <div className="overview-content">
                        <div className="super-card">
                            <div className="super-card-header">
                                <h2>Detailed Business Overview</h2>
                                <div className="overview-actions">
                                    {hasPermission('reports.view') && <button className="secondary-btn" onClick={() => handleTabChange('reports')}>View Reports</button>}
                                    {hasPermission('analytics.view') && <button className="secondary-btn" onClick={() => handleTabChange('analytics')}>Open Analytics</button>}
                                </div>
                            </div>
                            <div className="insight-pulse-row">
                                <div className="pulse-card">
                                    <div className="pulse-label">Conversion</div>
                                    <div className="pulse-value">{conversionRate}%</div>
                                    <div className="pulse-sub">Active vs total loans</div>
                                    <div className="sparkbar">
                                        {[70, 82, 65, 90, conversionRate].map((v, i) => (
                                            <span key={i} style={{ height: `${Math.min(100, v)}%` }} />
                                        ))}
                                    </div>
                                </div>
                                <div className="pulse-card">
                                    <div className="pulse-label">Pending Rate</div>
                                    <div className="pulse-value warning">{pendingRate}%</div>
                                    <div className="pulse-sub">{stats.pendingLoans || 0} loans waiting</div>
                                    <div className="sparkbar warning">
                                        {[pendingRate, 22, 18, 15, 12].map((v, i) => (
                                            <span key={i} style={{ height: `${Math.min(100, v)}%` }} />
                                        ))}
                                    </div>
                                </div>
                                <div className="pulse-card">
                                    <div className="pulse-label">Avg Ticket</div>
                                    <div className="pulse-value">₹{avgTicketSize.toLocaleString('en-IN')}</div>
                                    <div className="pulse-sub">Per active loan</div>
                                    <div className="sparkbar info">
                                        {[avgTicketSize / 1000, 12, 14, 9, 11].map((v, i) => (
                                            <span key={i} style={{ height: `${Math.min(100, v)}%` }} />
                                        ))}
                                    </div>
                                </div>
                                <div className="pulse-card">
                                    <div className="pulse-label">Compliance</div>
                                    <div className="pulse-value success">{complianceScore}%</div>
                                    <div className="pulse-sub">Pending vs processed</div>
                                    <div className="sparkbar success">
                                        {[complianceScore, 88, 86, 90, 92].map((v, i) => (
                                            <span key={i} style={{ height: `${Math.min(100, v)}%` }} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="overview-insights">
                                {hasPermission('loans.view') && (
                                    <div className="insight-card success">
                                        <div className="insight-label">Loan Approval Rate</div>
                                        <div className="insight-value">{approvalRate}%</div>
                                        <div className="insight-sub">Across {totalLoanCount} reviewed loans</div>
                                    </div>
                                )}
                                {hasPermission('loans.view') && (
                                    <div className="insight-card warning">
                                        <div className="insight-label">Pending Loan Share</div>
                                        <div className="insight-value">{pendingRate}%</div>
                                        <div className="insight-sub">{stats.pendingLoans || 0} awaiting decision</div>
                                    </div>
                                )}
                                <div className="insight-card info">
                                    <div className="insight-label">Avg Disbursal / Client</div>
                                    <div className="insight-value">₹{avgDisbursalPerUser.toLocaleString('en-IN')}</div>
                                    <div className="insight-sub">{stats.totalDisbursed || '₹0'} total disbursed</div>
                                </div>
                                {hasPermission('tickets.manage') && (
                                    <div className="insight-card alert">
                                        <div className="insight-label">Support Backlog</div>
                                        <div className="insight-value">{openTicketBacklog}</div>
                                        <div className="insight-sub">Open tickets to triage</div>
                                    </div>
                                )}
                            </div>

                            {/* Role-focused mini dashboards */}
                            {adminSession?.role === 'sales' && (
                                <div className="role-focus-panel sales">
                                    <div className="role-panel-head">
                                        <div>
                                            <p className="eyebrow">Sales Pipeline</p>
                                            <h3>Stay on top of your leads</h3>
                                        </div>
                                        <div className="role-actions">
                                            <button className="secondary-btn" onClick={() => handleTabChange('leads')}>Go to Leads</button>
                                            <button className="primary-btn" onClick={() => handleTabChange('leads')}>Add Lead</button>
                                        </div>
                                    </div>
                                    <div className="role-kpi-grid">
                                        <div className="role-kpi">
                                            <span>Total Leads</span>
                                            <strong>{leadSummary.total}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Assigned to Me</span>
                                            <strong>{leadSummary.myCount}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Unassigned</span>
                                            <strong>{leadSummary.unassigned}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Qualified</span>
                                            <strong>{leadSummary.byStatus.qualified || 0}</strong>
                                        </div>
                                    </div>
                                    <div className="role-pills">
                                        <div className="pill-stat">New: {leadSummary.byStatus.new || 0}</div>
                                        <div className="pill-stat warn">Contacted: {leadSummary.byStatus.contacted || 0}</div>
                                        <div className="pill-stat info">Converted: {leadSummary.byStatus.converted || 0}</div>
                                        <div className="pill-stat muted">Lost: {leadSummary.byStatus.lost || 0}</div>
                                    </div>
                                </div>
                            )}

                            {(adminSession?.role === 'branch' || adminSession?.role === 'accounts') && (
                                <div className="role-focus-panel branch">
                                    <div className="role-panel-head">
                                        <div>
                                            <p className="eyebrow">Disbursement Desk</p>
                                            <h3>Ready to disburse loans</h3>
                                        </div>
                                        <div className="role-actions">
                                            <button className="primary-btn" onClick={() => handleTabChange('loans')}>Open Loan Queue</button>
                                        </div>
                                    </div>
                                    <div className="role-kpi-grid">
                                        <div className="role-kpi">
                                            <span>Pending Approval</span>
                                            <strong>{stats.pendingLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Active Loans</span>
                                            <strong>{stats.activeLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Total Disbursed</span>
                                            <strong>{stats.totalDisbursed || '₹0'}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>System Health</span>
                                            <strong>{stats.systemHealth}</strong>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {adminSession?.role === 'collection' && (
                                <div className="role-focus-panel collection">
                                    <div className="role-panel-head">
                                        <div>
                                            <p className="eyebrow">Collections Control</p>
                                            <h3>Monitor repayments and follow-ups</h3>
                                        </div>
                                        <div className="role-actions">
                                            <button className="secondary-btn" onClick={() => handleTabChange('loans')}>View Active Loans</button>
                                        </div>
                                    </div>
                                    <div className="role-kpi-grid">
                                        <div className="role-kpi">
                                            <span>Active Loans</span>
                                            <strong>{stats.activeLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Pending Loans</span>
                                            <strong>{stats.pendingLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Total Loans</span>
                                            <strong>{stats.totalLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Support Tickets</span>
                                            <strong>{stats.openTickets || 0}</strong>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="overview-grid enhanced-overview-grid">
                                {hasPermission('users.view_all') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('users')}>
                                        <div className="overview-icon blue">👥</div>
                                        <div>
                                            <h4>{stats.totalUsers?.toLocaleString?.() || stats.totalUsers}</h4>
                                            <p>Total Clients</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('admins.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('admins')}>
                                        <div className="overview-icon purple">🔐</div>
                                        <div>
                                            <h4>{stats.totalAdmins || 0}</h4>
                                            <p>Administrator Accounts</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('analytics.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('analytics')}>
                                        <div className="overview-icon green">💳</div>
                                        <div>
                                            <h4>{stats.totalDisbursed || '₹0'}</h4>
                                            <p>Total Disbursed</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('loans.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('loans')}>
                                        <div className="overview-icon purple">📈</div>
                                        <div>
                                            <h4>{stats.totalLoans || 0}</h4>
                                            <p>Total Loans</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('activity.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('activity')}>
                                        <div className="overview-icon orange">⏳</div>
                                        <div>
                                            <h4>{stats.pendingLoans || stats.pendingApprovals || 0}</h4>
                                            <p>Pending Loans</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('loans.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('loans')}>
                                        <div className="overview-icon blue">📡</div>
                                        <div>
                                            <h4>{stats.activeLoans || 0}</h4>
                                            <p>Active Loans</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('leads.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('leads')}>
                                        <div className="overview-icon pink">🎯</div>
                                        <div>
                                            <h4>Leads</h4>
                                            <p>New Opportunities</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('tickets.manage') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('support')}>
                                        <div className="overview-icon purple">📜</div>
                                        <div>
                                            <h4>{stats.openTickets || 0}</h4>
                                            <p>Open Support Tickets</p>
                                        </div>
                                    </div>
                                )}

                                <div className="overview-item">
                                    <div className="overview-icon green">🩺</div>
                                    <div>
                                        <h4>{stats.systemHealth || 'Excellent'}</h4>
                                        <p>System Health</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'admins' && hasPermission('admins.view') && (
                    <>
                        <div className="super-card">
                            <div className="super-card-header">
                                <h2>Administrator Accounts</h2>
                                <div className="header-actions">
                                    <button className="action-header-btn create-admin" onClick={() => setShowCreateAdminModal(true)}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="8.5" cy="7" r="4"></circle>
                                            <line x1="20" y1="8" x2="20" y2="14"></line>
                                            <line x1="23" y1="11" x2="17" y2="11"></line>
                                        </svg>
                                        Create Admin
                                    </button>
                                    <button className="action-header-btn create-user" onClick={() => setShowCreateUserModal(true)}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                        Create User
                                    </button>
                                    <button className="action-header-btn settings" onClick={() => handleTabChange('settings')}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="3"></circle>
                                            <path d="M12 1v6m0 6v6M7.05 7.05l4.95 4.95m0 0l4.95 4.95M7.05 16.95l4.95-4.95m0 0l4.95-4.95"></path>
                                        </svg>
                                        Settings
                                    </button>
                                </div>
                            </div>
                            <div className="super-table-container">
                                <table className="super-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Status</th>
                                            <th>Last Login</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {admins.map(admin => (
                                            <tr key={admin.id}>
                                                <td className="admin-name">{admin.name}</td>
                                                <td className="admin-email">{admin.email}</td>
                                                <td>{getRoleBadge(admin.role)}</td>
                                                <td>
                                                    <button
                                                        className={`status-badge ${admin.status === 'Active' ? 'status-success' : 'status-danger'}`}
                                                        style={{ border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                                                        onClick={() => toggleAdminStatus(admin.id, admin.status)}
                                                        title="Click to toggle status"
                                                    >
                                                        {admin.status}
                                                    </button>
                                                </td>
                                                <td className="admin-date">{admin.lastLogin}</td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button className="action-btn edit" title="Edit" onClick={() => handleEditAdmin(admin)}>
                                                            Edit
                                                        </button>
                                                        <button className="action-btn delete" title="Delete" onClick={() => handleDeleteAdmin(admin.id)}>
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'users' && hasPermission('users.view_all') && (
                    <div className="super-card">
                        <div className="super-card-header user-header">
                            <div className="header-left">
                                <h2>User Management</h2>
                                <p className="header-sub">Search, create, and manage client users in one place.</p>
                            </div>
                            <div className="header-actions search-row">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search by name, email, phone..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                />
                                <button className="secondary-btn" onClick={fetchAllData}>Search</button>
                                <button className="action-header-btn create-user" onClick={() => setShowCreateUserModal(true)}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="8.5" cy="7" r="4"></circle>
                                        <line x1="20" y1="8" x2="20" y2="14"></line>
                                        <line x1="23" y1="11" x2="17" y2="11"></line>
                                    </svg>
                                    Create User
                                </button>
                            </div>
                        </div>
                        <div className="super-table-container">
                            <table className="super-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Status</th>
                                        <th>KYC Status</th>
                                        <th>Joined</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentUsers.map(user => (
                                        <tr key={user.id}>
                                            <td className="admin-name">{user.name}</td>
                                            <td className="admin-email">{user.email}</td>
                                            <td>
                                                <button
                                                    className={`status-badge ${user.status === 'Active' ? 'status-success' :
                                                        user.status === 'Restricted' ? 'status-warning' :
                                                            'status-danger'
                                                        }`}
                                                    style={{ border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                                                    onClick={() => {
                                                        const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
                                                        changeUserStatus(user.id, newStatus);
                                                    }}
                                                    title="Click to toggle status"
                                                >
                                                    {user.status}
                                                </button>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${user.kyc === 'completed' ? 'status-success' :
                                                    user.kyc === 'in_progress' ? 'status-warning' :
                                                        user.kyc === 'rejected' ? 'status-danger' :
                                                            'status-pending'
                                                    }`}>
                                                    {user.kyc === 'completed' ? 'Verified' :
                                                        user.kyc === 'in_progress' ? 'In Progress' :
                                                            user.kyc === 'rejected' ? 'Rejected' :
                                                                'Pending'}
                                                </span>
                                            </td>
                                            <td className="admin-date">{user.joinDate}</td>
                                            <td>
                                                <div className="action-buttons">
                                                    {(hasPermission('users.temp_password') || adminSession?.role === 'super_admin') && (
                                                        <button className="action-btn edit" title="Temp Password" onClick={() => openTempPasswordModal(user)}>
                                                            Temp Pass
                                                        </button>
                                                    )}
                                                    <button className="action-btn view" title="View Details" onClick={() => handleViewUser(user)}>
                                                        View
                                                    </button>
                                                    <button className="action-btn edit" title="Edit User" onClick={() => handleEditUser(user)}>
                                                        Edit
                                                    </button>
                                                    <button className="action-btn delete" title="Delete User" onClick={() => handleDeleteUser(user.id)}>
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'logs' && hasPermission('logs.view') && (
                    <div className="super-card">
                        <div className="super-card-header">
                            <h2>System Activity Logs</h2>
                            <button className="export-btn">
                                📤 Export Logs
                            </button>
                        </div>
                        <div className="logs-container">
                            {systemLogs.map(log => (
                                <div key={log.id} className={`log-item ${getSeverityClass(log.severity)}`}>
                                    <div className="log-icon">
                                        {log.severity === 'success' && '✅'}
                                        {log.severity === 'warning' && '⚠️'}
                                        {log.severity === 'error' && '❌'}
                                        {log.severity === 'info' && 'ℹ️'}
                                    </div>
                                    <div className="log-content">
                                        <div className="log-header">
                                            <h4>{log.action}</h4>
                                            <span className="log-time">{log.time}</span>
                                        </div>
                                        <p className="log-details">{log.details}</p>
                                        <span className="log-user">By: {log.user}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Access Denied Guard for dynamic tabs */}
                {activeTab !== 'overview' && tabPermissions[activeTab] && !hasPermission(tabPermissions[activeTab]) && (
                    <div className="super-card access-denied">
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚫</div>
                            <h2>Access Denied</h2>
                            <p>You do not have the required permissions to view this section.</p>
                            <button className="primary-btn" style={{ marginTop: '20px' }} onClick={() => handleTabChange('overview')}>
                                Return to Overview
                            </button>
                        </div>
                    </div>
                )}

                {/* New Tabs */}
                {activeTab === 'analytics' && hasPermission('analytics.view') && <AnalyticsTab adminSession={adminSession} />}
                {activeTab === 'permissions' && hasPermission('permissions.manage') && <PermissionsTab adminSession={adminSession} />}
                {activeTab === 'roles' && hasPermission('roles.manage') && <RolesTab adminSession={adminSession} />}
                {activeTab === 'settings' && hasPermission('settings.manage') && <SettingsTab adminSession={adminSession} />}
                {activeTab === 'database' && hasPermission('database.manage') && <DatabaseTab adminSession={adminSession} />}
                {activeTab === 'security' && hasPermission('security.manage') && <SecurityTab />}
                {activeTab === 'activity' && hasPermission('activity.view') && <ActivityMonitorTab adminSession={adminSession} />}
                {activeTab === 'reports' && hasPermission('reports.view') && <ReportsTab />}
                {activeTab === 'templates' && hasPermission('templates.manage') && <TemplatesTab />}
                {activeTab === 'support' && hasPermission('tickets.manage') && <SupportTab adminSession={adminSession} />}
                {activeTab === 'financials' && hasPermission('reports.view') && <FinancialTab />}
                {activeTab === 'api' && hasPermission('api.manage') && <ApiManagementTab adminSession={adminSession} />}
                {activeTab === 'notifications' && hasPermission('notifications.manage') && <NotificationsTab />}
                {activeTab === 'health' && hasPermission('system.health') && <HealthTab />}
                {activeTab === 'payments' && hasPermission(tabPermissions['payments']) && <PaymentGatewayTab adminSession={adminSession} />}
                {activeTab === 'kyc' && hasPermission(tabPermissions['kyc']) && <KycManagementTab adminSession={adminSession} />}
                {activeTab === 'loans' && hasPermission(tabPermissions['loans']) && <LoansTab adminSession={adminSession} initialActiveView="loans" />}
                {activeTab === 'leads' && hasPermission(tabPermissions['leads']) && <LeadsTab adminSession={adminSession} />}
            </div>

            {/* Modals */}
            <CreateAdminModal
                isOpen={showCreateAdminModal}
                onClose={() => setShowCreateAdminModal(false)}
                onSuccess={handleAdminCreated}
                createdBy={adminSession?.userId}
            />

            <CreateUserModal
                isOpen={showCreateUserModal}
                onClose={() => setShowCreateUserModal(false)}
                onSuccess={handleUserCreated}
                createdBy={adminSession?.userId}
            />

            <EditAdminModal
                isOpen={showEditAdminModal}
                onClose={() => setShowEditAdminModal(false)}
                onSuccess={fetchAllData}
                admin={editingAdmin}
                adminData={adminSession}
                onImpersonate={handleImpersonate}
            />

            <EditUserModal
                isOpen={showEditUserModal}
                onClose={() => setShowEditUserModal(false)}
                onSuccess={fetchAllData}
                user={editingUser}
                adminData={adminSession}
                onImpersonate={handleImpersonate}
            />

            <ViewUserModal
                isOpen={showViewUserModal}
                onClose={() => setShowViewUserModal(false)}
                user={viewingUser}
                adminData={adminSession}
            />

            {showTempPassModal && tempPassUser && (
                <div className="system-modal-overlay" onClick={() => setShowTempPassModal(false)}>
                    <div className="system-modal-content bento-look" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
                        <div className="system-modal-header">
                            <h3>⏳ Temporary Password</h3>
                            <button className="modal-close-btn" onClick={() => setShowTempPassModal(false)}>×</button>
                        </div>
                        <div className="system-modal-body">
                            <div className="form-group">
                                <label>User</label>
                                <input type="text" value={tempPassUser.name} disabled />
                            </div>
                            <div className="form-group">
                                <label>Password</label>
                                <input
                                    type="text"
                                    value={tempPassword}
                                    onChange={(e) => setTempPassword(e.target.value)}
                                    placeholder="Enter temporary password"
                                />
                                <small className="form-hint">Share this with the user; it will expire automatically.</small>
                            </div>
                            <div className="form-group">
                                <label>Validity</label>
                                <select value={tempValidity} onChange={(e) => setTempValidity(Number(e.target.value))}>
                                    <option value={10}>10 minutes</option>
                                    <option value={20}>20 minutes</option>
                                    <option value={30}>30 minutes</option>
                                </select>
                            </div>
                        </div>
                        <div className="system-modal-footer">
                            <button className="secondary-btn" onClick={() => setShowTempPassModal(false)}>Cancel</button>
                            <button className="primary-btn" onClick={handleCreateTempPassword}>Create</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Modal for Alerts/Confirmations */}
            {modalConfig.show && (
                <div className="system-modal-overlay">
                    <div className="system-modal-content bento-look">
                        <div className="system-modal-header">
                            <h3>{modalConfig.title}</h3>
                            <button className="close-x" onClick={closeModal}>&times;</button>
                        </div>
                        <div className="system-modal-body">
                            <p>{modalConfig.message}</p>
                        </div>
                        <div className="system-modal-footer">
                            {modalConfig.type === 'confirm' && (
                                <button className="modal-btn-cancel" onClick={closeModal}>
                                    {modalConfig.cancelText || 'Cancel'}
                                </button>
                            )}
                            <button className="modal-btn-confirm" onClick={() => {
                                if (modalConfig.onConfirm) modalConfig.onConfirm();
                                closeModal();
                            }}>
                                {modalConfig.confirmText || 'OK'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SuperAdminDashboardPage;
